import { LOCIZE_NS } from '@avanta/shared/constants/common';
import {
  clearRecoveryCookies,
  getStoredRecoverySession,
  saveRecoveryCookies,
  validateCodeLength,
  validatePasswordMatch,
  validateVerificationCode,
} from '@avanta/shared/utils/auth';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { ROUTES } from 'constants/routes';
import { AuthService } from 'services/auth';
import { useTrackingByEmail } from 'tracking/hooks/useTrackingByEmail';
import { TrackingEventsType } from 'types/tracking';

const VERIFICATION_CODE_LENGTH = 6;

interface UseRecoverPasswordReturn {
  recoverPassword: (email: string) => Promise<void>;
  verifyCode: (code: string) => Promise<void>;
  resendCode: () => Promise<void>;
  changePassword: (password: string, confirmPassword: string) => Promise<void>;
  isLoading: boolean;
  error: Error | null;
  currentStep: number;
  userEmail: string;
}

export const useRecoverPassword = (): UseRecoverPasswordReturn => {
  const { t } = useTranslation(LOCIZE_NS.AUTH);
  const navigate = useNavigate();
  const { trackByEmail } = useTrackingByEmail();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [userEmail, setUserEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');

  const handleAsyncOperation = async (
    operation: () => Promise<void>,
    successMessage?: string
  ): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      await operation();
      if (successMessage) {
        toast.success(successMessage);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : t('Common.error', { ns: LOCIZE_NS.COMMON });
      setError(new Error(errorMessage));
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    const { email, code } = getStoredRecoverySession();
    if (email) {
      setUserEmail(email);
      if (code) {
        setVerificationCode(code);
        setCurrentStep(3);
      } else {
        setCurrentStep(2);
      }
    }
  }, []);
  const recoverPassword = async (email: string): Promise<void> => {
    await handleAsyncOperation(async () => {
      await AuthService.recoverPassword(email);
      setUserEmail(email);
      saveRecoveryCookies(email);
      setCurrentStep(2);
    }, t('RecoverPassword.success'));
  };

  const verifyCode = async (code: string): Promise<void> => {
    if (!userEmail || !validateCodeLength(code, VERIFICATION_CODE_LENGTH)) return;

    await handleAsyncOperation(async () => {
      await AuthService.verifyRecoveryCode({
        validation_code: code,
        email: userEmail,
      });
      setVerificationCode(code);
      saveRecoveryCookies(userEmail, code);
      setCurrentStep(3);
    }, t('RecoverPassword.Verification.success'));
  };

  const changePassword = async (password: string, confirmPassword: string): Promise<void> => {
    await handleAsyncOperation(async () => {
      validatePasswordMatch(password, confirmPassword);
      validateVerificationCode(verificationCode);

      await AuthService.changePassword({
        email: userEmail,
        password,
        validation_code: verificationCode,
      });
      await trackByEmail(userEmail, TrackingEventsType.USER_RESET_PASSWORD);
      clearRecoveryCookies();
      navigate(ROUTES.LOGIN);
    }, t('RecoverPassword.PasswordChange.success'));
  };

  const resendCode = async (): Promise<void> => {
    if (!userEmail) return;

    await handleAsyncOperation(async () => {
      await AuthService.resendRecoveryCode(userEmail);
    }, t('RecoverPassword.Verification.resend.success'));
  };

  return {
    recoverPassword,
    verifyCode,
    resendCode,
    changePassword,
    isLoading,
    error,
    currentStep,
    userEmail,
  };
};
